import { DISPLAY_KEY_WHITESPACE, trimWhitespace } from './normalizationWhitespace';

// chartTheme.js
// Every styling decision the six plotly renderers used to each hardcode, in one
// place. Pure — no React, no plotly import — so it can be unit-tested and read
// without a browser.
//
// THE LAYERING RULE, top wins: a user's saved `chart_settings` override, then
// these theme defaults, then plotly's own defaults. A renderer therefore passes
// what the user chose and lets the builders below fill the rest; it never
// re-hardcodes a colour or a margin of its own.
//
// ONE AXIS, ALWAYS. Nothing here can produce a second y-axis and nothing here
// should be extended to: two measures on one frame make the reader compare two
// unrelated scales that have been made to look comparable. Two measures mean two
// charts, or one indexed to a common base.
//
// The chart surface is dark in BOTH app themes — every renderer paints its own
// near-black card — so there is one series palette, stepped against that dark
// surface, rather than a light set and a dark set that would disagree about the
// same card.

// The categorical palette. SLOT ORDER IS THE CONTRACT: series N always takes
// slot N, so a filter that drops a series can never repaint the ones that
// remain. Past the last slot the palette is not cycled as-is (two series in one
// colour is the worst failure a legend can have): see `seriesColor` for the
// derived colours a later lap takes.
//
// Every step is computed, not chosen by eye. Each sits inside the dark
// lightness band, above the chroma floor, above 3:1 against the card, and far
// enough from EVERY other slot — not merely its neighbours — to survive
// simulated protanopia and deuteranopia. The blue is Decode's brand blue and
// does not move; the rest are the smallest nudges of the previous palette that
// clear those gates.
//
// What changed and why:
//   - the second blue is gone outright. It sat four slots from the first and
//     was 4.3 ΔE from it, which is indistinguishable to full-colour vision, let
//     alone anything else. Orange takes that slot.
//   - green, amber and pink were all too LIGHT for a dark card and have been
//     stepped down into the band. Amber had the furthest to come, so it reads
//     as gold rather than as the old butter yellow; that is the band, not a
//     change of mind about the hue.
//   - pink and green used to collapse onto each other under deuteranopia at
//     1.2 ΔE — the worst failure in the old set. They are now 8.1 apart at
//     their closest, which is the whole palette's floor.
//
// Verified with the data-viz palette validator against this surface: all checks
// pass on the ALL-pairs list, dark and light. The list is at its ceiling — no
// slot's chroma can be raised without dropping another pair back under the
// separation floor.
export const CHART_SERIES_COLORS = Object.freeze([
  '#4E85EB', // blue   — brand, slot 1, unchanged
  '#D23062', // red
  '#8551B5', // purple
  '#A55402', // orange — replaces the dropped duplicate blue
  '#42AC78', // green
  '#BB8D0B', // amber
  '#C178B3', // pink
]);

// Ink. NEVER pure white on a dark card: #FFFFFF against a near-black surface
// glares and drags the eye off the data. Callers pass the app theme's tokens;
// these are the same values, kept here so a pure function has a default.
export const CHART_INK = Object.freeze({ primary: '#F0F2F5', secondary: '#ADB5BD' });

// Grid lines sit just above the surface — present when the eye looks for them,
// invisible when it does not. The baseline is the one line allowed to be
// stronger, because zero is a real reference and the other lines are not.
const GRID_COLOR = 'rgba(255, 255, 255, 0.07)';
const BASELINE_COLOR = 'rgba(255, 255, 255, 0.14)';

const HOVER_BG = '#262B35';
const HOVER_BORDER = 'rgba(255, 255, 255, 0.10)';

// The colour of the card the charts are drawn on, used to cut a hairline gap
// between fills that touch — pie slices, stacked segments. A gap made of the
// surface reads as a gap; a gap made of a grey line reads as a border.
export const CHART_SURFACE_RING = '#0A0C10';

const TICK_SIZE = 11;
const AXIS_TITLE_SIZE = 11;
const LEGEND_SIZE = 11;

// Series N takes slot N. Past the end of the palette a series starts a new
// LAP: it takes the slot it wraps to, tinted towards white by a fixed step per
// lap, so series 8 is a paler slot 1 rather than slot 1 again or a grey shared
// with everything after it. Line and scatter marks on a later lap also change
// shape (dash, marker symbol — `seriesDash`, `seriesSymbol`), so two series
// that differ only by tint still differ by mark. Slot N always means series N,
// whatever the lap, so dropping a series never repaints the rest.
//
// The emailed PNG is drawn by the backend from the same palette contract; it
// applies this same lap rule.
const LAP_TINT = 0.32;
const MAX_TINT = 0.72;

const hexToRgb = (hex) => {
  const match = /^#?([0-9a-f]{6})$/i.exec(String(hex || '').trim());
  if (!match) return null;
  const number = parseInt(match[1], 16);
  return [(number >> 16) & 255, (number >> 8) & 255, number & 255];
};

// A hex colour at a given opacity, for a fill that must not hide the line
// drawn over it. A colour that is not six-digit hex is returned as it is.
export const withAlpha = (hex, alpha) => {
  const rgb = hexToRgb(hex);
  return rgb ? `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, ${alpha})` : hex;
};

// Mix a hex colour towards white. A colour that is not six-digit hex (a saved
// override in another notation) is returned as it is.
export const tintTowardsWhite = (hex, amount) => {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;
  const channel = (value) => Math.round(value + (255 - value) * amount).toString(16).padStart(2, '0');
  return `#${rgb.map(channel).join('')}`;
};

const seriesPalette = (palette) => (
  Array.isArray(palette) && palette.length ? palette : CHART_SERIES_COLORS
);

export const seriesLap = (palette, index) => Math.floor(index / seriesPalette(palette).length);

export const seriesColor = (palette, index) => {
  const list = seriesPalette(palette);
  const lap = Math.floor(index / list.length);
  const base = list[index % list.length];
  return lap === 0 ? base : tintTowardsWhite(base, Math.min(MAX_TINT, LAP_TINT * lap));
};

export const SERIES_DASHES = Object.freeze(['solid', 'dash', 'dot', 'dashdot']);
export const SERIES_SYMBOLS = Object.freeze(['circle', 'diamond', 'square', 'triangle-up']);

export const seriesDash = (palette, index) => SERIES_DASHES[seriesLap(palette, index) % SERIES_DASHES.length];
export const seriesSymbol = (palette, index) => SERIES_SYMBOLS[seriesLap(palette, index) % SERIES_SYMBOLS.length];

// ── display settings ─────────────────────────────────────────────────────────
// The saved chart settings a renderer honours. Everything here is PRESENTATION:
// how the chart is drawn, never what it says. Title and axis labels are content
// and belong to the logic block that produced the chart — a stored override of
// those would outlive a rewrite of the analysis and label the new chart with
// the old words — so they are not in this list and are never overlaid, even if
// an older save left them stored.
export const CHART_DISPLAY_SETTINGS = Object.freeze([
  'grid',
  'show_legend',
  'x_axis_rotation',
  'show_points',
  'line_width',
  'horizontal',
  'stacked',
  'bar_width',
]);

// One stable key per child of a combined chart: the child's normalised title,
// with `#2`, `#3`, ... appended to later children that repeat an earlier one's
// title. A combined chart's children are an ordered list with no identifier,
// and a later run may reorder, insert or drop children. Keyed by position, a
// saved setting would follow the position onto whichever chart occupies it
// next; keyed by title it follows the chart, and a child the logic block
// renames simply draws with defaults. The backend PNG renderer applies the
// same keys (data_functions/charts.py combined_child_keys) — the two must agree.
export const combinedChildKeys = (elements) => {
  const seen = new Map();
  return (Array.isArray(elements) ? elements : []).map((child) => {
    const title = child && typeof child === 'object' && child.title != null ? child.title : '';
    // Never empty: Firestore rejects an empty map key.
    const base = trimWhitespace(title, DISPLAY_KEY_WHITESPACE).toLowerCase() || 'untitled';
    const count = (seen.get(base) || 0) + 1;
    seen.set(base, count);
    return count === 1 ? base : `${base}#${count}`;
  });
};

// ── numbers ──────────────────────────────────────────────────────────────────
// The unit comes from the chart contract first: `x_unit` / `y_unit` sit beside
// the labels and are authoritative. Only when the chart carries no unit is the
// label consulted, and then conservatively — a unit is claimed only when the
// label says so outright, because guessing "premium" means pounds is how a
// ratio ends up rendered as £0.82.
const UNIT_MARKS = Object.freeze({
  GBP: { prefix: '£' },
  '£': { prefix: '£' },
  USD: { prefix: '$' },
  $: { prefix: '$' },
  EUR: { prefix: '€' },
  '€': { prefix: '€' },
  '%': { suffix: '%' },
  PCT: { suffix: '%' },
  PERCENT: { suffix: '%' },
  PERCENTAGE: { suffix: '%' },
});
const GBP_LABEL = /£|\bgbp\b/i;
const PCT_LABEL = /%|\bpct\b|\bpercent(age)?\b/i;

// `{ prefix, suffix, symbol }`: `symbol` says the unit is a currency or percent
// mark, which ticks can carry; a unit that is a word ("policies") is written
// after a hovered value but would clutter every tick, so ticks leave it to the
// axis title.
export const axisUnit = (label, unit) => {
  const key = typeof unit === 'string' ? unit.trim() : '';
  if (key) {
    const mark = UNIT_MARKS[key.toUpperCase()] || UNIT_MARKS[key];
    if (mark) return { prefix: '', suffix: '', ...mark, symbol: true };
    return { prefix: '', suffix: ` ${key}`, symbol: false };
  }
  const text = typeof label === 'string' ? label : '';
  if (PCT_LABEL.test(text)) return { prefix: '', suffix: '%', symbol: true };
  if (GBP_LABEL.test(text)) return { prefix: '£', suffix: '', symbol: true };
  return { prefix: '', suffix: '', symbol: false };
};

// The value as it is, grouped for reading: 125.5 -> "£125.5", 0.004 ->
// "0.004", 2500000 -> "£2,500,000". This is what a hovered point and an end
// label print — the one place the exact number can be read off a chart, so it
// is never rounded to a magnitude. A textual value (a date or category
// coordinate) is returned as written.
export const formatExact = (value, { prefix = '', suffix = '' } = {}) => {
  if (value === null || value === undefined || value === '') return '';
  const number = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(number)) return typeof value === 'string' ? value : '';
  const sign = number < 0 ? '-' : '';
  return `${sign}${prefix}${Math.abs(number).toLocaleString('en-GB', { maximumFractionDigits: 6 })}${suffix}`;
};

// When a chart does not carry explicit ticks, plotly picks the tick values from
// the live range and formats them in magnitude style (1.4k, 2.5M) with the
// unit's symbol. Explicit chart ticks are authoritative, however: they are part
// of the stored Function result and must survive browser rendering and image
// export.
export const tickNumberProps = (label, unit) => {
  const { prefix, suffix, symbol } = axisUnit(label, unit);
  return {
    exponentformat: 'B',
    showexponent: 'all',
    ...(symbol && prefix ? { tickprefix: prefix } : {}),
    ...(symbol && suffix ? { ticksuffix: suffix } : {}),
  };
};

// ── axes ─────────────────────────────────────────────────────────────────────
const PLOTLY_AXIS_TYPES = Object.freeze({
  linear: 'linear',
  log: 'log',
  datetime: 'date',
  categorical: 'category',
  unknown: null,
});

const hasAxisType = (scale) => (
  scale == null || Object.prototype.hasOwnProperty.call(PLOTLY_AXIS_TYPES, scale)
);

// Plotly has no native symlog/logit/asinh/function axis. Drawing one of those
// as linear would silently change the meaning of the chart, so callers render
// this message in place of the plot.
export const chartAxisError = (chart) => {
  for (const axisName of ['x', 'y']) {
    const scale = chart?.[`${axisName}_scale`];
    if (!hasAxisType(scale)) {
      return `The ${axisName}-axis uses the unsupported “${scale}” scale and cannot be displayed accurately in the browser.`;
    }
  }
  return null;
};

const normaliseAxisTick = (value, scale) => {
  if (typeof value !== 'string' || !['linear', 'log'].includes(scale)) return value;
  const number = Number(value);
  return value.trim() !== '' && Number.isFinite(number) ? number : value;
};

const axisContractProps = ({ scale = null, ticks = [], tickLabels = [] } = {}) => {
  if (!hasAxisType(scale)) {
    throw new Error(`Unsupported chart axis scale: ${scale}`);
  }
  const type = scale == null ? null : PLOTLY_AXIS_TYPES[scale];
  const explicitTicks = Array.isArray(ticks) && ticks.length > 0;
  return {
    ...(type ? { type } : {}),
    ...(explicitTicks ? {
      tickmode: 'array',
      tickvals: ticks.map((value) => normaliseAxisTick(value, scale)),
      ticktext: tickLabels,
    } : {}),
  };
};

// `automargin` is what keeps the margins below honest: the base margin is tight
// and plotly grows only the side that actually has a long tick label or a title
// to fit, so the plot area stays as large as the labels allow.
const axis = ({
  label,
  unit,
  rotation = 0,
  showGrid = false,
  measure = false,
  autoScale = false,
  metadata = {},
  ink = CHART_INK,
}) => {
  const contract = axisContractProps(metadata);
  const hasExplicitTicks = contract.tickmode === 'array';
  return {
    title: { text: label || '', font: { size: AXIS_TITLE_SIZE, color: ink.secondary } },
    tickfont: { size: TICK_SIZE, color: ink.secondary },
    tickangle: rotation,
    showgrid: showGrid,
    gridcolor: GRID_COLOR,
    gridwidth: 1,
    zeroline: measure && contract.type !== 'log',
    zerolinecolor: BASELINE_COLOR,
    zerolinewidth: 1,
    showline: false,
    linecolor: BASELINE_COLOR,
    automargin: true,
    ...(measure && !hasExplicitTicks ? tickNumberProps(label, unit) : {}),
    ...(autoScale ? { autorange: true } : {}),
    ...contract,
  };
};

// Grid lines belong to the MEASURE axis and only to it. On the usual upright
// chart that is y, which draws the horizontal rules asked for; on a horizontal
// bar chart the measure has moved to x and the rules move with it, because a
// grid drawn along the category axis measures nothing.
export const buildAxes = ({
  categoryLabel,
  measureLabel,
  categoryUnit,
  measureUnit,
  categoryRotation = 0,
  measureRotation = 0,
  showGrid = true,
  horizontal = false,
  autoScale = false,
  categoryMetadata = {},
  measureMetadata = {},
  ink = CHART_INK,
}) => {
  const categoryAxis = axis({
    label: categoryLabel,
    unit: categoryUnit,
    rotation: categoryRotation,
    showGrid: false,
    autoScale,
    metadata: categoryMetadata,
    ink,
  });
  const measureAxis = axis({
    label: measureLabel,
    unit: measureUnit,
    rotation: measureRotation,
    showGrid,
    measure: true,
    autoScale,
    metadata: measureMetadata,
    ink,
  });
  return horizontal
    ? { xaxis: measureAxis, yaxis: categoryAxis }
    : { xaxis: categoryAxis, yaxis: measureAxis };
};

// Both axes of a scatter carry a measure, so both get the number treatment —
// but only y draws grid lines, so the plot keeps one direction of rule.
export const buildMeasureAxes = ({
  xLabel,
  yLabel,
  xUnit,
  yUnit,
  xRotation = 0,
  yRotation = 0,
  showGrid = true,
  autoScale = false,
  xMetadata = {},
  yMetadata = {},
  ink = CHART_INK,
}) => ({
  xaxis: axis({ label: xLabel, unit: xUnit, rotation: xRotation, showGrid: false, measure: true, autoScale, metadata: xMetadata, ink }),
  yaxis: axis({ label: yLabel, unit: yUnit, rotation: yRotation, showGrid, measure: true, autoScale, metadata: yMetadata, ink }),
});

// ── layout ───────────────────────────────────────────────────────────────────
// No space is reserved at the top for a title: every frame that draws one of
// these charts prints the title in its own header above the plot, so plotly's
// title stays empty and that space goes to the data. The top margin exists only
// when there is a legend to sit in it.
const LEGEND_ROOM = 34;
const BASE_MARGIN = { l: 8, r: 16, t: 8, b: 8 };

export const buildLayout = ({
  axes = {},
  showLegend = false,
  ink = CHART_INK,
  annotations = [],
  labelGutter = 0,
  extraMargin = {},
  ...rest
}) => ({
  title: { text: '', font: { color: ink.primary } },
  ...axes,
  paper_bgcolor: 'transparent',
  plot_bgcolor: 'transparent',
  font: { color: ink.secondary },
  showlegend: showLegend,
  legend: {
    orientation: 'h',
    x: 0,
    xanchor: 'left',
    y: 1.02,
    yanchor: 'bottom',
    font: { size: LEGEND_SIZE, color: ink.secondary },
    bgcolor: 'rgba(0, 0, 0, 0)',
    borderwidth: 0,
  },
  hoverlabel: {
    bgcolor: HOVER_BG,
    bordercolor: HOVER_BORDER,
    font: { color: ink.primary, size: 12 },
  },
  // A hover target wider than the mark: a 2px line is a hard thing to point at.
  hoverdistance: 12,
  margin: {
    ...BASE_MARGIN,
    t: BASE_MARGIN.t + (showLegend ? LEGEND_ROOM : 0),
    r: BASE_MARGIN.r + labelGutter,
    ...extraMargin,
  },
  annotations,
  autosize: true,
  ...rest,
});

// ── marks ────────────────────────────────────────────────────────────────────
// Bars must not touch. `bar_width` is the user's setting and drives the gap
// directly, but a floor keeps a sliver of card visible between neighbours even
// when the setting asks for none.
const MIN_BAR_GAP = 0.18;
const MIN_GROUP_GAP = 0.08;

export const barGaps = ({ barWidth, stacked }) => {
  const width = Math.max(0, Math.min(1, typeof barWidth === 'number' ? barWidth : 0.6));
  return {
    bargap: Math.max(MIN_BAR_GAP, 1 - width),
    bargroupgap: stacked ? 0 : Math.max(MIN_GROUP_GAP, (1 - width) / 2),
  };
};

export const LINE_WIDTH = 2;
export const POINT_SIZE = 8;
export const SCATTER_MARKER = { size: 9, opacity: 0.8 };

// ── hover ────────────────────────────────────────────────────────────────────
// The value is pre-formatted into `customdata` rather than described with a d3
// spec, so hover text and direct labels come out of the same function and can
// never drift apart. Hover shows the EXACT value with its unit; magnitude
// rounding (1.4k, 2.5M) is for ticks, which plotly formats itself.
export const hoverValues = (values, label, unit) => {
  const marks = axisUnit(label, unit);
  return values.map((value) => formatExact(value, marks));
};

// Under `x unified` plotly already prints the shared x value once at the top of
// the box and the series name at the head of each row, so a line's template
// carries the value alone.
export const HOVER_UNIFIED = '%{customdata}<extra></extra>';

// Per-point hover has no such header, so a bar names its own category. Which
// axis holds that category depends on the orientation.
export const barHoverTemplate = (horizontal) => (
  `${horizontal ? '%{y}' : '%{x}'}<br>%{customdata}<extra>%{fullData.name}</extra>`
);

// A scatter point is two measures, so both are formatted and shown together.
export const SCATTER_HOVER = '%{customdata[0]} · %{customdata[1]}<extra>%{fullData.name}</extra>';

// A scatter coordinate may be a date or a category as well as a number; a
// textual one is shown as written rather than forced through Number().
export const scatterHoverValues = (xs, ys, xLabel, yLabel, xUnit, yUnit) => {
  const xMarks = axisUnit(xLabel, xUnit);
  const yMarks = axisUnit(yLabel, yUnit);
  return xs.map((x, index) => [formatExact(x, xMarks), formatExact(ys[index], yMarks)]);
};

// ── direct labels ────────────────────────────────────────────────────────────
// A label at the end of each line beats a legend the eye has to shuttle to —
// but only while the lines actually end apart. Two series converging to the
// same value would stack their labels on top of each other, which is worse than
// no label at all, so the endpoints must be separated by a real fraction of the
// plot's height before any label is drawn. Four series is the ceiling: past
// that the right-hand gutter costs more plot area than the labels return.
const MAX_DIRECT_LABELS = 4;
const MIN_ENDPOINT_SEPARATION = 0.09; // fraction of the y range
export const LABEL_GUTTER = 76;

export const buildDirectLabels = ({ series, palette, ink = CHART_INK, xScale, yScale }) => {
  // Plotly uses data coordinates for points, but base-10 exponents for
  // annotations on logarithmic axes. Separation must use that same space.
  if (yScale && !['linear', 'log', 'unknown'].includes(yScale)) return [];
  const labelY = (value) => {
    if (value === null || value === undefined || value === '') return null;
    const number = Number(value);
    if (!Number.isFinite(number) || (yScale === 'log' && number <= 0)) return null;
    return yScale === 'log' ? Math.log10(number) : number;
  };
  const ends = (series || [])
    .map((entry, index) => {
      const xs = entry?.x || [];
      const ys = entry?.y || [];
      const last = ys.length - 1;
      if (last < 0) return null;
      // A missing final point has no end to label; Number(null) would put the
      // label at zero.
      const y = labelY(ys[last]);
      if (y === null) return null;
      let x = xs[last];
      if (xScale === 'log') {
        const number = Number(x);
        if (!Number.isFinite(number) || number <= 0) return null;
        x = Math.log10(number);
      }
      return { name: entry.name, x, y, index };
    })
    .filter(Boolean);

  if (ends.length < 2 || ends.length > MAX_DIRECT_LABELS || ends.length !== (series || []).length) {
    return [];
  }

  const allY = (series || []).flatMap((entry) => (entry?.y || []).map(labelY)).filter(value => value !== null);
  const range = Math.max(...allY) - Math.min(...allY);
  if (!(range > 0)) return [];

  const sorted = [...ends].sort((a, b) => a.y - b.y);
  for (let i = 1; i < sorted.length; i += 1) {
    if ((sorted[i].y - sorted[i - 1].y) / range < MIN_ENDPOINT_SEPARATION) return [];
  }

  return ends.map((end) => ({
    x: end.x,
    y: end.y,
    xref: 'x',
    yref: 'y',
    xanchor: 'left',
    yanchor: 'middle',
    xshift: 8,
    showarrow: false,
    align: 'left',
    // The swatch carries the identity so the text can stay in reading ink; a
    // label printed in its series colour competes with the mark it names.
    text: `<span style="color:${seriesColor(palette, end.index)}">●</span> ${end.name}`,
    font: { size: LEGEND_SIZE, color: ink.secondary },
  }));
};

// ── export ───────────────────────────────────────────────────────────────────
// A PNG leaves the app for a slide deck, a report or an email, where the dark
// card does not follow it — so the export is styled as a PRINT, not as a
// screenshot: white paper, dark ink, the title inside the bitmap, room around
// the plot, larger type than the screen needs, an axis line, and tick marks.
// The series colours are the same steps, which is why they were checked
// against contrast rather than against one background.
export const EXPORT_INK = '#1A1E26';
export const EXPORT_INK_SOFT = '#4A5060';
export const EXPORT_PAPER = '#FFFFFF';
const EXPORT_GRID = 'rgba(0, 0, 0, 0.08)';
const EXPORT_AXIS_LINE = 'rgba(0, 0, 0, 0.28)';
export const EXPORT_FONT = 'Inter, "Segoe UI", Helvetica, Arial, sans-serif';
const EXPORT_TICK_SIZE = 15;
const EXPORT_AXIS_TITLE_SIZE = 16;
const EXPORT_LEGEND_SIZE = 15;
const EXPORT_TITLE_SIZE = 26;
// The margin the print always keeps; plotly's automargin grows a side further
// when a long tick label or a title needs it.
const EXPORT_MARGIN = { l: 64, r: 48, t: 40, b: 64 };
const EXPORT_LEGEND_ROOM = 40;
const EXPORT_TITLE_ROOM = 52;

const exportAxis = (source) => (source ? {
  ...source,
  title: {
    ...(source.title || {}),
    font: { ...(source.title?.font || {}), size: EXPORT_AXIS_TITLE_SIZE, color: EXPORT_INK_SOFT },
    standoff: 14,
  },
  tickfont: { ...(source.tickfont || {}), size: EXPORT_TICK_SIZE, color: EXPORT_INK },
  ticks: 'outside',
  ticklen: 5,
  tickcolor: EXPORT_AXIS_LINE,
  showline: true,
  linecolor: EXPORT_AXIS_LINE,
  linewidth: 1,
  gridcolor: EXPORT_GRID,
  zerolinecolor: 'rgba(0, 0, 0, 0.22)',
} : source);

// `title` is the words to print at the top of the bitmap — the chart's own
// title, prefixed by its combined-chart ancestry when it has one. The screen
// layout never carries a title (the frame prints it); the print always does.
export const buildExportLayout = (layout, { title = '' } = {}) => {
  const text = typeof title === 'string' ? title.trim() : '';
  const hasLegend = layout.showlegend === true;
  return {
    ...layout,
    title: {
      text,
      x: 0,
      xanchor: 'left',
      xref: 'paper',
      y: 1,
      yanchor: 'top',
      pad: { t: 18, l: 0 },
      font: { family: EXPORT_FONT, size: EXPORT_TITLE_SIZE, color: EXPORT_INK },
    },
    paper_bgcolor: EXPORT_PAPER,
    plot_bgcolor: EXPORT_PAPER,
    font: { ...(layout.font || {}), family: EXPORT_FONT, color: EXPORT_INK },
    ...(layout.xaxis ? { xaxis: exportAxis(layout.xaxis) } : {}),
    ...(layout.yaxis ? { yaxis: exportAxis(layout.yaxis) } : {}),
    legend: {
      ...(layout.legend || {}),
      font: { ...(layout.legend?.font || {}), size: EXPORT_LEGEND_SIZE, color: EXPORT_INK },
    },
    annotations: (layout.annotations || []).map((note) => ({
      ...note,
      font: { ...(note.font || {}), size: EXPORT_LEGEND_SIZE, color: EXPORT_INK },
    })),
    margin: {
      ...EXPORT_MARGIN,
      t: EXPORT_MARGIN.t + (text ? EXPORT_TITLE_ROOM : 0) + (hasLegend ? EXPORT_LEGEND_ROOM : 0),
      r: EXPORT_MARGIN.r + Math.max(0, (layout.margin?.r || 0) - BASE_MARGIN.r),
    },
    hovermode: false,
  };
};

export const CHART_CONFIG = Object.freeze({ displayModeBar: false, displaylogo: false });
